import { push } from "connected-react-router";

import { of } from "rxjs";
import { ofType } from "redux-observable";
import { ajax } from "rxjs/ajax";
import {
    filter,
    debounceTime,
    tap,
    map,
    catchError,
    mergeMap,
    switchMap,
    startWith,
    delay,
    pairwise,
} from "rxjs/operators";

import { combineEpics } from "redux-observable";

import h from "../../helpers";
import { logTreeSearch, logDataDiscoverySearch } from "../../helpers/gaTrackers";
import { handleSetSearchedFields, loadOrMergeFields } from "../../actions/fieldTreeActions";
import { filterBooleanFields } from "../../reducers/search";
import { fieldMatchesSearchString } from "../../reducers/fields";

interface IAction {
    text: string;
    bundles: Array<any>;
    isModal?: boolean;
    enhancedSearch?: boolean;
    serverFilterOn?: boolean;
    conceptualOn?: boolean;
}

interface IWordVec {
    matches: Array<Array<object>>;
    didYouMean: Array<Array<object>>;
    error: string;
}

interface IDeApiSearch {
    result: Array<any>; //dynamic so use any type
    conceptualSearchMatches: Array<string>;
    conceptualActive: boolean;
    didYouMeans: Array<{ key: string; value: Array<string> }>;
}

// Debounce a user's typing and stop them from searching for more than 1 character
const typeEpic = (action$, state$) =>
    action$.pipe(
        ofType("TREESEARCH_USER_TYPED"),
        filter(
            (action: IAction) => action.text.length > 2 || action.text.length == 0 || action.text.toLowerCase() == "tv"
        ),
        debounceTime(1000),
        mergeMap((action: IAction) => {
            // If search text is empty, clear search results immediately
            if (action.text.length == 0) {
                return of({ type: "CLEAR_TREE_SEARCH" }, { type: "SET_TREESEARCH", text: "" });
            }

            // Check if boolean search is enabled
            const state = state$.value;
            const booleanSearchEnabled = !!(
                state.session &&
                Array.isArray(state.session.enabledFeatures) &&
                state.session.enabledFeatures.includes("use-boolean-field-search")
            );

            let type = "SET_TREESEARCH";

            // Boolean search takes precedence if enabled
            if (booleanSearchEnabled && action.text.length > 0) {
                type = "SET_BOOLEAN_TREESEARCH";
            } else if (action.isModal) {
                type = "SET_MODAL_TREESEARCH";
            } else if (action.conceptualOn) {
                // Always use server-side search for conceptual (faster than client-side)
                // Feature flag only controls display mode (flat list vs tree view)
                type = "SET_ENHANCED_TREESEARCH";
            } else if (!action.conceptualOn) {
                // Always use server-side search for non-conceptual (faster than client-side)
                // Feature flag only controls display mode (flat list vs tree view)
                type = "SET_TREESEARCH";
            }

            return of({
                type,
                text: action.text.toLowerCase(),
            });
        })
    );

// Run conceptual search after they finish typing if appropriate
const enhancedConceptualEpic = (action$, state$) =>
    action$.pipe(
        ofType("SET_ENHANCED_TREESEARCH"),
        filter((action: IAction) => action.text.length > 2 || action.text.toLowerCase() == "tv"), // Run if available, even if turned off - it's easier for when they turn on
        map((action: IAction) => action.text.toLowerCase()),
        tap((text: string) => logTreeSearch(text)),
        map((text: string) => text),
        filter((word: string) => /\S/.test(word)),
        mergeMap((text: string) => {
            // read flags from state before making the ajax call so we can pick the URL
            const wordvecServer = !!(
                state$ &&
                state$.value &&
                state$.value.session &&
                Array.isArray(state$.value.session.enabledFeatures) &&
                state$.value.session.enabledFeatures.includes("wordvec-server-filtering")
            );

            const editFields = state$.value.vars?.prevEditable ? 1 : 0;
            const url = wordvecServer
                ? `/Search/WordVectorConceptualServer/${encodeURIComponent(text)}?editFields=${editFields}`
                : `/Search/WordVectorConceptualServer/${encodeURIComponent(text)}?editFields=${editFields}`;
            return ajax.getJSON(url).pipe(
                map(data => {
                    const response = data as unknown as IWordVec;
                    const safeDispatch =
                        (globalThis as any).store && (globalThis as any).store.dispatch
                            ? (globalThis as any).store.dispatch
                            : f => f;
                    const typedData = data as { matchedFields?: any; tableKey?: any; fields?: any };
                    if (wordvecServer) {
                        try {
                            if ((data as any).matchedFields) {
                                const tableKey = (data as any).tableKey || 0; // placeholder if not provided

                                // call helper (will internally dispatch multiple actions)
                                const typedData = data as { matchedFields?: any; tableKey?: any };
                                handleSetSearchedFields(safeDispatch, typedData.matchedFields, tableKey);
                            }
                        } catch (e) {
                            // don't break the epic if the helper call fails; log for visibility
                            // eslint-disable-next-line no-console
                            console.warn("handleFieldTreeLoadSuccess call failed", e);
                        }
                    }
                    safeDispatch(loadOrMergeFields(typedData.fields, 0));
                    return {
                        type: "ENHANCED_WORDVEC_RESULTS",
                        text,
                        matches: response.matches,
                        error: response.error || false,
                    };
                }),
                catchError(error =>
                    of({
                        type: "WORDVEC_ERROR",
                        payload: error.xhr.response,
                        error: true,
                    })
                ),
                startWith({
                    type: "WORDVEC_SEARCHING",
                })
            );
        })
    );

// Run conceptual search after they finish typing if appropriate
const enhancedSearchEpic = (action$, state$) =>
    action$.pipe(
        ofType("SET_TREESEARCH"),
        filter((action: IAction) => action.text.length > 2 || action.text.toLowerCase() == "tv"), // Run if available, even if turned off - it's easier for when they turn on
        map((action: IAction) => action.text.toLowerCase()),
        tap((text: string) => logTreeSearch(text)),
        map((text: string) => text),
        filter((word: string) => /\S/.test(word)),
        mergeMap((text: string) => {
            // read flags from state before making the ajax call so we can pick the URL
            const wordvecServer = !!(
                state$ &&
                state$.value &&
                state$.value.session &&
                Array.isArray(state$.value.session.enabledFeatures) &&
                state$.value.session.enabledFeatures.includes("wordvec-server-filtering")
            );
            const editFields = state$.value.vars?.prevEditable ? 1 : 0;
            const url = wordvecServer
                ? `/Search/WordVectorServer/${encodeURIComponent(text)}?editFields=${editFields}`
                : `/Search/WordVectorServer/${encodeURIComponent(text)}?editFields=${editFields}`;

            return ajax.getJSON(url).pipe(
                map(data => {
                    const response = data as unknown as IWordVec;
                    const safeDispatch =
                        (globalThis as any).store && (globalThis as any).store.dispatch
                            ? (globalThis as any).store.dispatch
                            : f => f;
                    // call helper (will internally dispatch multiple actions)
                    const typedData = data as { matchedFields?: any; tableKey?: any; fields?: any };

                    if (wordvecServer) {
                        try {
                            if ((data as any).matchedFields) {
                                const tableKey = (data as any).tableKey || 0; // placeholder if not provided
                                handleSetSearchedFields(safeDispatch, typedData.matchedFields, tableKey);
                            }
                        } catch (e) {
                            // don't break the epic if the helper call fails; log for visibility
                            // eslint-disable-next-line no-console
                            console.warn("handleFieldTreeLoadSuccess call failed", e);
                        }
                    }
                    safeDispatch(loadOrMergeFields(typedData.fields, 0));
                    return {
                        type: "ENHANCED_WORDVEC_RESULTS",
                        text,
                        matches: response.matches,
                        error: response.error || false,
                    };
                }),
                catchError(error =>
                    of({
                        type: "WORDVEC_ERROR",
                        payload: error.xhr.response,
                        error: true,
                    })
                ),
                startWith({
                    type: "WORDVEC_SEARCHING",
                })
            );
        })
    );

// Run conceptual search after they finish typing if appropriate
const modalConceptualEpic = action$ =>
    action$.pipe(
        ofType("SET_MODAL_TREESEARCH"),
        filter((action: IAction) => action.text.length > 2 || action.text.toLowerCase() == "tv"), // Run if available, even if turned off - it's easier for when they turn on
        map((action: IAction) => action.text.toLowerCase()),
        tap((text: string) => logTreeSearch(text)),
        map((text: string) => text),
        filter((word: string) => /\S/.test(word)),
        mergeMap((text: string) =>
            ajax.getJSON(`/Search/WordVector/${text}`).pipe(
                map(data => {
                    const response = data as unknown as IWordVec;
                    return {
                        type: "MODAL_WORDVEC_RESULTS",
                        text,
                        matches: response.matches,
                        error: response.error || false,
                    };
                }),
                catchError(error =>
                    of({
                        type: "MODAL_WORDVEC_ERROR",
                        payload: error.xhr.response,
                        error: true,
                    })
                ),
                startWith({
                    type: "MODAL_WORDVEC_SEARCHING",
                })
            )
        )
    );

const discoveryEpic = (action$, state$) =>
    action$.pipe(
        ofType("SUBMIT_DISCOVERYSEARCH"),
        mergeMap((action: IAction) => {
            const state = state$.value;
            const score = state.search.discoveryConceptualScoreThreshold;
            const nummatches = state.search.discoveryConceptualMaxMatches;
            logDataDiscoverySearch(action.text.toLowerCase());
            return ajax.getJSON(`/deapi/search/${action.text.toLowerCase()}/${score}/${nummatches}`).pipe(
                // This is word/score/nummatches - FieldTree defaults to 40/45
                map(data => {
                    const response = data as unknown as IDeApiSearch;
                    return {
                        type: "DISCOVERYSEARCH_RESULTS",
                        text: action.text,
                        result: response.result,
                        conceptualSearchMatches: response.conceptualSearchMatches,
                        conceptualActive: response.conceptualActive,
                        didYouMeans: response.didYouMeans,
                        error: false,
                    };
                }),
                catchError(error =>
                    of({
                        type: "DISCOVERYSEARCH_ERROR",
                        payload: error.xhr.response,
                        error: true,
                    })
                ),
                /* .takeUntil(action$.ofType('WORDVEC_CANCELED')) */
                startWith({ type: "DISCOVERYSEARCH_SEARCHING" })
            );
        })
    );

// Bundle only search with blank term.
const discoveryBundleEpic = action$ =>
    action$.pipe(
        ofType("SUBMIT_DISCOVERY_BUNDLE_ONLY_SEARCH"),
        mergeMap((action: IAction) => {
            const bundlesCsv = action.bundles.join(",");
            const searchUrl = "/deapi/searchall_bundles?" + h.serialize({ bundlesCsv });
            //const state = store.getState();
            return ajax.getJSON(searchUrl).pipe(
                // This is word/score/nummatches - FieldTree defaults to 40/45
                map(data => {
                    const response = data as unknown as IDeApiSearch;
                    return {
                        type: "DISCOVERYSEARCH_RESULTS",
                        text: "", //action.text,
                        result: response.result,
                        /*
                            conceptualSearchMatches: response.conceptualSearchMatches,
                            conceptualActive: response.conceptualActive,
                            didYouMeans: response.didYouMeans,
                            */
                        error: false,
                    };
                }),
                catchError(error =>
                    of({
                        type: "DISCOVERYSEARCH_ERROR",
                        payload: error.xhr.response,
                        error: true,
                    })
                ),
                /* .takeUntil(action$.ofType('WORDVEC_CANCELED')) */
                startWith({ type: "DISCOVERYSEARCH_SEARCHING" })
            );
        })
    );

const redirAfterDiscoveryEpic = action$ =>
    action$.pipe(
        ofType("DISCOVERYSEARCH_RESULTS"),
        map((action: IAction) =>
            push((action.text == "" ? "/discover_blank/" : "/discover/") + encodeURIComponent(action.text))
        )
    );

// Re-trigger search when conceptual toggle is clicked
const retriggerSearchOnConceptualToggle = (action$, state$) =>
    action$.pipe(
        ofType("CONCEPTUAL_TOGGLE", "MODAL_CONCEPTUAL_TOGGLE"),
        delay(10), // Small delay to ensure reducer has updated the state
        map((action: any) => {
            // Determine if this is a modal toggle
            const isModal = action.type === "MODAL_CONCEPTUAL_TOGGLE";

            // Read state AFTER the reducer has processed the toggle
            const state = state$.value;
            const searchText = isModal ? state.search.modalTreeSearch : state.search.treeSearch;
            const conceptualOn = isModal ? state.search.modalConceptualTurnedOn : state.search.conceptualTurnedOn;
            const wordvecServer = !!(
                state.session &&
                Array.isArray(state.session.enabledFeatures) &&
                state.session.enabledFeatures.includes("wordvec-server-filtering")
            );

            // Only re-trigger if there's actual search text
            if (searchText && searchText.length > 0) {
                return {
                    type: "TREESEARCH_USER_TYPED",
                    text: searchText,
                    isModal,
                    conceptualOn,
                    serverFilterOn: wordvecServer,
                };
            }
            return { type: "NOOP" };
        }),
        filter((action: any) => action.type !== "NOOP")
    );

// Boolean operators that should never be treated as search terms
// Boolean search epic - completely separate code path
const booleanSearchEpic = (action$, state$) =>
    action$.pipe(
        ofType("SET_BOOLEAN_TREESEARCH"),
        filter((action: IAction) => action.text.length > 2 || action.text.toLowerCase() == "tv"),
        map((action: IAction) => action.text.toLowerCase()),
        tap((text: string) => logTreeSearch(text)),
        filter((word: string) => /\S/.test(word)),
        switchMap((text: string) => {
            const safeDispatch =
                (globalThis as any).store && (globalThis as any).store.dispatch
                    ? (globalThis as any).store.dispatch
                    : f => f;
            safeDispatch({ type: "CLEAR_TREE_SEARCH" });

            const conceptualOn = !!state$?.value?.search?.conceptualTurnedOn;
            const editFields = state$?.value?.vars?.prevEditable ? 1 : 0;

            // When conceptual is ON use the server-side expansion endpoint so the URL stays short.
            // When conceptual is OFF use the plain boolean endpoint with the raw expression.
            const endpoint = conceptualOn ? "BooleanFieldSearchConceptual" : "BooleanFieldSearch";

            const url = `/Search/${endpoint}/${encodeURIComponent(text)}?editFields=${editFields}&useClientMatcher=true`;

            return ajax.getJSON(url).pipe(
                map(data => {
                    const response = data as {
                        fields?: { [key: string]: any };
                        synonymMap?: { [word: string]: string[] };
                        error?: string | boolean;
                        tableKey?: number;
                    };

                    const result = response.error
                        ? { matchedFields: {}, matches: [] }
                        : filterBooleanFields(
                              response.fields || {},
                              text,
                              conceptualOn ? response.synonymMap || {} : {},
                              fieldMatchesSearchString
                          );

                    const tableKey = response.tableKey || 0;

                    const displayFields = result.matchedFields;

                    handleSetSearchedFields(safeDispatch, displayFields, tableKey);
                    // Search results must never replace the complete field tree.
                    // Merge them only so selected search results can still be resolved.
                    safeDispatch({
                        type: "MERGE_CHILD_FIELDS",
                        childFields: result.matchedFields,
                    });

                    return {
                        type: "BOOLEAN_SEARCH_RESULTS",
                        text,
                        matches: result.matches,
                        error: response.error || false,
                    };
                }),
                catchError(error =>
                    of({
                        type: "BOOLEAN_SEARCH_ERROR",
                        payload: error.xhr?.response || error.message,
                        error: true,
                    })
                ),
                startWith({
                    type: "WORDVEC_SEARCHING",
                })
            );
        })
    );

// The two feature flags that affect how field-tree search results are fetched and displayed.
const SEARCH_DISPLAY_FEATURES = ["use-boolean-field-search", "wordvec-server-filtering"];

// Re-trigger the active search whenever either of the two search-display feature flags
// changes (e.g. an admin toggles them on/off via the features admin page).  Without this,
// the search results cached in Redux state were built with the old flag combination and
// the display would only update after a full page navigation.
const retriggerSearchOnFeatureToggle = (_action$, state$) =>
    state$.pipe(
        // Emit pairs of [previousFeatures, currentFeatures] so we can detect changes
        map((state: any) => (state.session.enabledFeatures || []) as Array<string>),
        pairwise(),
        filter(([prev, curr]: [Array<string>, Array<string>]) =>
            // Only proceed when at least one of the two relevant flags changed
            SEARCH_DISPLAY_FEATURES.some(f => prev.includes(f) !== curr.includes(f))
        ),
        delay(10), // Let the reducer settle before reading the updated state
        map(() => {
            const state = state$.value;
            const searchText = state.search.treeSearch;
            const conceptualOn = !!state.search.conceptualTurnedOn;
            const wordvecServer = !!(
                state.session &&
                Array.isArray(state.session.enabledFeatures) &&
                state.session.enabledFeatures.includes("wordvec-server-filtering")
            );

            if (searchText && searchText.length > 0) {
                return {
                    type: "TREESEARCH_USER_TYPED",
                    text: searchText,
                    isModal: false,
                    conceptualOn,
                    serverFilterOn: wordvecServer,
                };
            }
            return { type: "NOOP" };
        }),
        filter((action: any) => action.type !== "NOOP")
    );
const clearBooleanSearchEpic = (action$, state$) =>
    action$.pipe(
        ofType("CLEAR_TREESEARCH"),
        filter(() => {
            const features = state$.value.session?.enabledFeatures;

            return Array.isArray(features) && features.includes("use-boolean-field-search");
        }),
        map(() => ({ type: "CLEAR_TREE_SEARCH" }))
    );

const searchEpics = combineEpics(
    typeEpic,
    modalConceptualEpic,
    enhancedSearchEpic,
    enhancedConceptualEpic,
    discoveryEpic,
    discoveryBundleEpic,
    redirAfterDiscoveryEpic,
    retriggerSearchOnConceptualToggle,
    retriggerSearchOnFeatureToggle,
    booleanSearchEpic,
    clearBooleanSearchEpic
);
export { searchEpics };
export default searchEpics;
